import { useRef, useState } from 'react';
import { Paperclip, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '@/lib/api';
import { Button } from '@/ui/Button';

export interface UploadedFile {
  id: string;
  name: string;
  size: number;
  mimeType: string;
  url: string;
}

const readableSize = (bytes: number) =>
  bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / 1024 / 1024).toFixed(1)} MB`;

/**
 * Picks a file, uploads it, and hands back the stored URL. The value is the
 * URL, not the File, so a form can be submitted as JSON like every other one
 * and the upload has already succeeded by the time it is.
 */
export function FileUpload({ value, name, onChange, accept, disabled }: {
  /** The stored URL, or empty. */
  value: string;
  /** Display name for an already-stored file. */
  name?: string;
  onChange: (file: UploadedFile | null) => void;
  accept?: string;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function pick(file: File | undefined) {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      onChange(await api.upload<UploadedFile>('/uploads', file));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  }

  if (value) {
    return (
      <div className="flex items-center gap-2 rounded-[var(--radius-control)] border border-line bg-surface px-3 py-2">
        <Paperclip className="size-4 shrink-0 text-ink-muted" aria-hidden />
        <a
          href={value}
          target="_blank"
          rel="noreferrer"
          className="min-w-0 flex-1 truncate text-sm text-secondary hover:underline"
        >
          {name || t('common.attachment')}
        </a>
        {!disabled && (
          <Button
            size="sm"
            variant="ghost"
            aria-label={t('common.remove')}
            icon={<X className="size-3.5" aria-hidden />}
            onClick={() => onChange(null)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      <input
        ref={input}
        type="file"
        accept={accept}
        className="sr-only"
        onChange={(e) => void pick(e.target.files?.[0])}
      />
      <Button
        type="button"
        variant="outline"
        loading={busy}
        disabled={disabled}
        icon={<Paperclip className="size-4" aria-hidden />}
        onClick={() => input.current?.click()}
      >
        {t('common.chooseFile')}
      </Button>
      {error && <span role="alert" className="text-xs text-danger">{error}</span>}
    </div>
  );
}

export { readableSize };
